import React, { useEffect } from "react"
import {
  Box,
  Tooltip,
  makeStyles,
  useTheme,
  useMediaQuery,
} from "@material-ui/core"
import CircularProgressBar from "src/components/circular-progress-bar"
import { goToFile } from "src/components/icons-svg"
import useASTStore from "src/v2/utils/AST-store"
import useStore from "src/v2/utils/store"

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    alignItems: "center",
    marginBottom: 4,
  },
  resourceBox: {
    position: "relative",
    display: "inline-flex",
    padding: "3px 3px",
    fontSize: 16,
    borderRadius: 8,
    transition: "box-shadow 0.2s, border 0.2s",
    cursor: "pointer",
    minWidth: 0,
    maxWidth: 220,
    [theme.breakpoints.down("xs")]: {
      fontSize: 14,
      maxWidth: 140,
    },
  },
  selected: {
    border: "3px solid #000",
    boxShadow: "2px 2px 2px #000000b8",
    padding: "1px 1px",
  },
  notSelected: {
    border: "1px solid rgba(0,0,0,0.047)",
  },
  progress: {
    position: "absolute",
    top: -4,
    right: -4,
  },
  goToFile: {
    cursor: "pointer",
    color: theme.palette.primary.main,
    textDecoration: "underline",
    paddingLeft: 5,
    display: "flex",
    alignItems: "center",
  },
}))

/**
 * @param props {{
 * resource: any,
 * showProgress?: boolean,
 * cardElementType?: string
 * }}
 */
export default function OneResource({
  resource,
  showProgress = true,
  cardElementType = "",
}) {
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"))
  const selectedResource = useASTStore((state) => state.selectedResource)
  const setSelectedFile = useASTStore((state) => state.setSelectedFile)
  const setSelectedResource = useASTStore((state) => state.setSelectedResource)
  const goto = useASTStore((state) => state.goto)
  const getProgressForResource = useASTStore((state) => state.getProgressForResource)
  const completedTasks = useStore((s) => s.completedTasks)

  // Re-run progress calculation when completedTasks changes
  useEffect(() => {
    // This will trigger a re-render and update progress bars
    // No-op body, just dependency on completedTasks
  }, [completedTasks])

  const showProgressCheckboxFormalMapping = window["config"]?.SHOW_PROGRESS_CHECKBOX_FORMAL_MAPPING === "YES"

  const { name, type, displayName } = resource
  const isSelected = resource?.displayName === selectedResource?.displayName

  const onGoToFile = () => {
    if (resource.declaredInFile) {
      setSelectedFile(resource.declaredInFile)
    }
    setSelectedResource(resource)

    if (resource.declaredInFile && resource.line) {
      setTimeout(() => {
        goto(resource.declaredInFile, resource.line)
      })
    }
  }

  const canGoTo =
    (Object.prototype.hasOwnProperty.call(resource, "inUse") &&
      resource["inUse"]?.length > 0) ||
    (!Object.prototype.hasOwnProperty.call(resource, "inUse") &&
      cardElementType !== "expandable-path-resources")

  const color_bg = resource.color_bg || "#f7fafc"
  const color_fg = resource.color_fg || "#222"

  const progressObj = getProgressForResource
    ? getProgressForResource(resource.astId)
    : { completed: 0, total: 0 }
  const progress =
    progressObj && progressObj.total
      ? Math.floor((progressObj.completed / progressObj.total) * 100)
      : 0

  return (
    <Box className={classes.root}>
      <Box position="relative">
        <Tooltip
          title={(displayName || name)?.length > 30 ? displayName || name : ""}
          arrow
        >
          <Box
            className={`${classes.resourceBox} ${
              isSelected ? classes.selected : classes.notSelected
            }`}
            style={{
              backgroundColor: color_bg,
              color: color_fg,
              cursor: canGoTo ? "pointer" : "not-allowed",
              maxWidth: isMobile ? 140 : 220,
              minWidth: 0,
            }}
            onClick={() => {
              if (canGoTo) onGoToFile()
            }}
          >
            {(displayName || name)?.length > 30
              ? `${(displayName || name).substring(0, 27)}...`
              : displayName || name}
            {showProgress && canGoTo && showProgressCheckboxFormalMapping && (
              <span className={classes.progress}>
                <CircularProgressBar
                  height={12}
                  width={12}
                  progress={progress}
                />
              </span>
            )}
          </Box>
        </Tooltip>
      </Box>
      {cardElementType === "expandable-path-resources" && (
        <Tooltip title="Go to file" arrow>
          <span
            className={classes.goToFile}
            onClick={onGoToFile}
            tabIndex={0}
            role="button"
          >
            {goToFile(20)}
          </span>
        </Tooltip>
      )}
    </Box>
  )
}
